import { Plus } from "lucide-react";
import { useTranslations } from "next-intl";
import type { CSSProperties, ReactNode } from "react";

import {
  CANVAS,
  CARD_CENTERS,
  MIGRATION_GROUPS,
  MIGRATION_TIMELINE,
  type MigrationChip,
  delay,
} from "./migration-data";

function chipKey(chip: MigrationChip): string {
  if (chip.kind === "brand") return chip.name;
  return chip.kind === "label" ? chip.key : "more";
}

function Chip(props: { readonly chip: MigrationChip }): ReactNode {
  const t = useTranslations("marketing.migrationStage.chips");
  const { chip } = props;
  if (chip.kind === "more") {
    return (
      <span className="mkmg-chip mkmg-chip-more">
        <Plus size={12} strokeWidth={2.4} aria-hidden />
        {t("more")}
      </span>
    );
  }
  const label = chip.kind === "brand" ? chip.name : t(chip.key);
  if (chip.kind !== "brand" || chip.picked !== true) return <span className="mkmg-chip">{label}</span>;
  return (
    <span className="mkmg-chip">
      <span className="mkmg-chip-on mkmg-fade" style={delay(MIGRATION_TIMELINE.active)} />
      <span>{label}</span>
    </span>
  );
}

/** One card per kind of old software, each ending in "all others"; the first card is being read. */
export function MigrationSources(): ReactNode {
  const t = useTranslations("marketing.migrationStage.groups");
  return (
    <div className="mkmg-board">
      {MIGRATION_GROUPS.map((group, index) => (
        <div
          key={group.key}
          className="mkmg-card"
          style={{ "--rows": group.rows.length } as CSSProperties}
        >
          {index === 0 ? (
            <span className="mkmg-ring mkmg-fade" style={delay(MIGRATION_TIMELINE.active)} aria-hidden />
          ) : null}
          <div className="mkmg-card-head">{t(group.key)}</div>
          <div className="mkmg-rows">
            {group.rows.map((row, r) => (
              <div key={r} className="mkmg-row">
                {row.map((chip) => (
                  <Chip key={chipKey(chip)} chip={chip} />
                ))}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/** Phone only, under the chat: every program per group as plain text. Chips would take too much height. */
export function MigrationSourcesCompact(): ReactNode {
  const tGroups = useTranslations("marketing.migrationStage.groups");
  const tChips = useTranslations("marketing.migrationStage.chips");
  const nameOf = (chip: MigrationChip): string => {
    if (chip.kind === "brand") return chip.name;
    return chip.kind === "label" ? tChips(chip.key) : tChips("more");
  };
  return (
    <div className="mkmg-list">
      {MIGRATION_GROUPS.map((group) => (
        <div key={group.key}>
          <div className="mkmg-list-head">{tGroups(group.key)}</div>
          <div className="mkmg-list-names">{group.rows.flat().map(nameOf).join(" · ")}</div>
        </div>
      ))}
    </div>
  );
}

/** Desktop only: a wire from every card into the chat; dots run along the one being read. */
export function MigrationWires(): ReactNode {
  const portX = CANVAS.chatX;
  const portY = CANVAS.h / 2;
  return (
    <svg
      className="mkmg-wires"
      viewBox={`0 0 ${CANVAS.w} ${CANVAS.h}`}
      preserveAspectRatio="none"
      aria-hidden
    >
      {CARD_CENTERS.map((y, index) => {
        const d = `M ${CANVAS.boardW} ${y} C ${CANVAS.boardW + 44} ${y}, ${portX - 44} ${portY}, ${portX} ${portY}`;
        return (
          <g key={y}>
            <path className="mkmg-wire" d={d} />
            {index === 0 ? (
              <path
                className="mkmg-wire-flow"
                d={d}
                style={{ animationDelay: `0ms, ${MIGRATION_TIMELINE.active}ms` }}
              />
            ) : null}
          </g>
        );
      })}
      <circle className="mkmg-port" cx={portX} cy={portY} r={5} />
    </svg>
  );
}
